import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import "bootstrap-icons/font/bootstrap-icons.css";
import BASE_URL from "../config";

const UserDetail = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const userIdOrUsername = queryParams.get("id");  // Get from URL either user id or username
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [loggedInUserRole, setLoggedInUserRole] = useState("");

  // This effect ensures that if no token is found, it redirects to the login page
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      window.location.href = "/login";
    }
  }, []);

  // Fetch logged-in user details
  useEffect(() => {
    const fetchLoggedInUser = async () => {
      const token = localStorage.getItem("token");
      try {
        const { data } = await axios.get(`${BASE_URL}api/user/profile`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setLoggedInUserRole(data.role);
      } catch (error) {
        Swal.fire("Error", "Failed to fetch user details", "error");
      }
    };

    fetchLoggedInUser();
  }, []);

  // Fetch user details based on id or username
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const token = localStorage.getItem("token");
        let response;

        // If it's a username, fetch by username
        if (isNaN(userIdOrUsername)) {
          response = await axios.get(`${BASE_URL}api/user/find-by-username/${userIdOrUsername}`, {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          });
        } else {
          // If it's an id, fetch by userId
          response = await axios.get(`${BASE_URL}api/user/find-one/${userIdOrUsername}`, {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          });
        }

        if (response.data) {
          setUser(response.data);
        }
      } catch (error) {
        Swal.fire("Error", error.response?.data?.message || "Failed to fetch user details", "error");
      }
    };

    if (userIdOrUsername) {
      fetchUser();
    }
  }, [userIdOrUsername]); // Dependency array to ensure it's called when userIdOrUsername changes

  // Update the browser URL with the username after successful fetch
  useEffect(() => {
    if (user) {
      // Replace spaces with hyphens for the username in the URL
      const formattedUsername = user.username.replace(/\s+/g, "-");
      navigate(`/user-detail?${formattedUsername}`, { replace: true });
    }
  }, [user, navigate]);

  if (!user) {
    return <div className="container mt-4"><h2>Loading user details...</h2></div>;
  }

  return (
    <div className="container mt-5 d-flex justify-content-center">
      <div className="card shadow-lg" style={{ width: "100%", borderRadius: "7px" }}>
        <div className="row g-0">
          <div className="col-md-4 text-center p-4" style={{ background: "linear-gradient(to bottom,rgb(89, 97, 145),rgb(134, 131, 130))", borderRadius: "12px 0 0 12px" }}>
            <img
              src={user.image_url || "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcSnFRPx77U9mERU_T1zyHcz9BOxbDQrL4Dvtg&s"}
              alt="User"
              className="rounded-circle mb-3"
              style={{ width: "100px", height: "100px" }}
            />
            <h4 className="text-white">{user.username}</h4>
            <p className="text-white-50">{user.role}</p>

            {user.role !== "admin" && (
              <button
                className="btn btn-primary"
                onClick={() => navigate(`/edit-profile?id=${user.id}`)}
              >
                Edit My Profile
              </button>
            )}
          </div>
          <div className="col-md-8 p-4">
            <h4 className="mb-3">Information</h4>
            <table className="table table-borderless">
              <tbody>
                <tr>
                  <th>Email:</th>
                  <td>
                    <a href={`mailto:${user.email}`} className="text-muted text-decoration-none">
                      {user.email}
                    </a>
                  </td>
                </tr>
                <tr>
                  <th>Phone:</th>
                  <td>
                    {user.phone ? (
                      <a href={`tel:${user.phone}`} className="text-muted text-decoration-none">
                        {user.phone}
                      </a>
                    ) : (
                      ""
                    )}
                  </td>
                </tr>
                <tr>
                  <th>Address:</th>
                  <td>{user.address ?? ""}</td>
                </tr>
                <tr>
                  <th>Status:</th>
                  <td>
                    <span className={`badge ${user.status === "active" ? "bg-success" : "bg-danger"}`}>
                      {user.status}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
            <hr />

            <div className="col-md-12 d-flex justify-content-between">
              <div>
                <h6 className="mb-1" style={{ color: "black" }}>Bio</h6>
                <p className="text-muted">{user.bio ?? ""}</p>
              </div>
            </div>

            <div className="col-md-12 d-flex justify-content-between">
              <div className="col-md-6">
                <h6 className="mb-1" style={{ color: "black" }}>Personal Email</h6>
                <p className="text-muted">
                  {user.personal_email ? (
                    <a href={`mailto:${user.personal_email}`} className="text-muted text-decoration-none">
                      {user.personal_email}
                    </a>
                  ) : (
                    "N/A"
                  )}
                </p>
              </div>

              <div className="col-md-6">
                <h6 className="mb-1" style={{ color: "black" }}>Last Login</h6>
                <p className="text-muted">
                  {user.last_login
                      ? new Date(user.last_login).toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })
                      : "N/A"}
                  {/* {user.last_login ?? ""} */}
                </p>
              </div>
            </div>

            <div className="col-md-12 d-flex justify-content-between">
              <div className="col-md-6">
                <h6 className="mb-1" style={{ color: "black" }}>Resign Date</h6>
                {user.resign_date && (
                  <p className="text-muted">
                    {/* {new Date(user.resign_date).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })} */}

                    {user.resign_date
                      ? new Date(user.resign_date).toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })
                      : "N/A"}
                  </p>
                )}

              </div>
              <div className="col-md-6">
                <h6 className="mb-1" style={{ color: "black" }}>Emergency Contact</h6>
                <p className="text-muted">
                  {user.emergency_phone ? (
                    <a href={`tel:${user.emergency_phone}`} className="text-muted text-decoration-none">
                      {user.emergency_phone}
                    </a>
                  ) : (
                    "N/A"
                  )}
                </p>
              </div>
            </div>

            <div className="col-md-12 d-flex justify-content-between">
              <div className="col-md-6">
                <h6 className="mb-1" style={{ color: "black" }}>Emergency Person Name</h6>
                <p className="text-muted">{user.emergency_person_name ?? "N/A"}</p>
              </div>
              <div className="col-md-6">
                <h6 className="mb-1" style={{ color: "black" }}>Emergency Relation</h6>
                <p className="text-muted">{user.emergency_person_relation ?? "N/A"}</p>
              </div>
            </div>

            <div className="col-md-12 d-flex justify-content-between">
              <div className="col-md-6">
                <h6 className="mb-1" style={{ color: "black" }}>Date Created</h6>
                <p className="text-muted">
                  {new Date(user.date_created).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </p>
              </div>
              <div className="col-md-6">
                <h6 className="mb-1" style={{ color: "black" }}>Date Updated</h6>
                <p className="text-muted">
                  {new Date(user.date_updated).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </p>
              </div>
            </div>

            <div className="mt-3">
              <a href={user.facebook_link || "#"} className="me-3 text-secondary"><i className="bi bi-facebook"></i></a>
              <a href={user.linkedIn_link || "#"} className="me-3 text-secondary"><i className="bi bi-linkedin"></i></a>
            </div>
            <a href="/users" className="btn btn-secondary mt-3">Back to Users</a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserDetail;
